import { NgClass } from '@angular/common';
import {
  type AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  type ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { LabelComponent } from '@reconcept-platform/shared/ui/standalone/label';
import { RcpPopoverDirective } from '@reconcept-platform/shared/ui/standalone/popover';

@Component({
  selector: '[section-expandable]',
  styleUrls: ['./section-expandable.component.scss'],
  template: `
    <!-- insert content of an element with property header or produce default header -->
    @if (showHeader) {
      <header (click)="clickExpand()" [ngClass]="headerClasses" [class.disabled]="!enabled">
        @if (enabled) {
          <fa-icon [icon]="faAngleRight"></fa-icon>
        }
        <div
          class="header-content ellipsis"
          [class.max-width-250]="constrictWidth"
          #titleEl
          [rcpPopover]="title"
          [rcpPopoverIsEnabled]="showTitleInToolTip"
        >
          <div #ref>
            <ng-content select="[header]"></ng-content>
          </div>
          @if (hasChildNodes) {
            <span>
              {{ title }}
            </span>
          }
          @if (label) {
            <rcp-label color="neutral">{{ label }}</rcp-label>
          }
        </div>
        <ng-content select="[actions]"></ng-content>
      </header>
    }

    <div class="section-expandable-content">
      <ng-content></ng-content>
    </div>
  `,
  imports: [NgClass, FaIconComponent, RcpPopoverDirective, LabelComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SectionExpandableComponent implements AfterViewInit {
  @Input() title: string;
  @Input() label: string;

  @Input() constrictWidth: boolean;

  @Input() headerClasses: 'small' | 'highlighted' | 'small highlighted';

  @Input() enabled = true;
  @Input() showHeader = true;

  @Input() expanded: boolean;

  @Output() clickHeader: EventEmitter<boolean> = new EventEmitter();
  @Output() expand: EventEmitter<boolean> = new EventEmitter();

  hasChildNodes: boolean;
  showTitleInToolTip: boolean;

  @ViewChild('ref') ref: ElementRef;
  @ViewChild('titleEl') titleElement: ElementRef;

  protected readonly faAngleRight = faAngleRight;

  @HostBinding('class.section-expandable-expanded')
  get showContent() {
    return this.expanded || !this.showHeader;
  }

  clickExpand() {
    if (this.enabled) {
      this.expanded = !this.expanded;
      this.expand.emit(this.expanded);
      this.clickHeader.emit();
    }
  }

  expandSection() {
    if (this.enabled) {
      this.expanded = true;
      this.expand.emit(this.expanded);
    }
  }

  ngAfterViewInit() {
    Promise.resolve().then(() => {
      this.hasChildNodes = this.ref?.nativeElement.childNodes.length === 0;

      if (this.titleElement) {
        const widthTitleHasOnScreen = this.titleElement.nativeElement.offsetWidth;
        const totalWidthTitleNeeds = this.titleElement.nativeElement.scrollWidth;

        // when the title width that is has on screen is smaller than needed, that means the user needs to scroll to see the entire tile
        this.showTitleInToolTip = widthTitleHasOnScreen < totalWidthTitleNeeds;
      }
    });
  }
}
